import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../services/api";
import { fetchEta, formatEta, formatDistance } from "../utils/eta";

const STATUS_STEPS = [
  { key: "pending", label: "Order Placed", icon: "📋", desc: "Your order has been received" },
  { key: "assigned", label: "Driver Assigned", icon: "🏍️", desc: "A driver has been assigned" },
  { key: "picked", label: "Picked Up", icon: "📦", desc: "Driver has picked up your order" },
  { key: "in_transit", label: "On The Way", icon: "🚀", desc: "Your order is on its way" },
  { key: "delivered", label: "Delivered", icon: "✅", desc: "Order delivered successfully" },
];

const vehicleIcon = (type) => {
  const map = { motorcycle: "🏍️", bicycle: "🚲", van: "🚐", truck: "🚛", bus: "🚌", lorry: "🚚" };
  return map[type] || "🚗";
};

function formatTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });
}


export default function TrackingPage() {
  const { token } = useParams();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const driverMarker = useRef(null);
  const destMarker = useRef(null);
  const pusherChannel = useRef(null);
  const orderRef = useRef(null);


  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [eta, setEta] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);

  // Confirmation flow state variables
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');


  // Keep ref in sync for Pusher callbacks
  useEffect(() => { orderRef.current = order; }, [order]);


  // ── Map helpers ──────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (mapInstance.current || !mapRef.current) return;
    const L = window.L;
    if (!L) return;
    const map = L.map(mapRef.current, { center: [1.2921, 36.8219], zoom: 13, zoomControl: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);
    mapInstance.current = map;
  }, []);


  const updateRiderPosition = useCallback(async (lat, lng, orderData) => {
    const L = window.L;
    if (!L || !mapInstance.current) return;


    const icon = L.divIcon({
      html: `<div style="background:#f97316;border:3px solid white;border-radius:50%;width:42px;height:42px;display:flex;align-items:center;justify-content:center;font-size:20px;box-shadow:0 2px 12px rgba(249,115,22,0.5);">${vehicleIcon(orderData?.rider?.vehicle_type)}</div>`,
      className: "", iconSize: [42, 42], iconAnchor: [21, 21],
    });


    if (driverMarker.current) {
      driverMarker.current.setLatLng([lat, lng]);
    } else {
      driverMarker.current = L.marker([lat, lng], { icon })
        .addTo(mapInstance.current)
        .bindPopup(`<b>${orderData?.rider?.name || "Your Driver"}</b>`);
    }


    if (!destMarker.current && orderData?.order?.delivery_lat && orderData?.order?.delivery_lng) {
      const destIcon = L.divIcon({
        html: `<div style="font-size:28px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">📍</div>`,
        className: "", iconSize: [28, 28], iconAnchor: [14, 28],
      });
      destMarker.current = L.marker(
        [orderData.order.delivery_lat, orderData.order.delivery_lng],
        { icon: destIcon }
      ).addTo(mapInstance.current).bindPopup("Delivery Location");
    }


    mapInstance.current.flyTo([lat, lng], 14, { duration: 1 });


    if (orderData?.order?.delivery_lat && orderData?.order?.delivery_lng) {
      setEtaLoading(true);
      const result = await fetchEta(lat, lng, orderData.order.delivery_lat, orderData.order.delivery_lng);
      setEta(result);
      setEtaLoading(false);
    }
  }, []);


  // ── Fetch order ──────────────────────────────────────────────────
  const fetchOrder = useCallback(async () => {
    try {
      const res = await api.get(`/track/${token}`);
      const data = res.data;
      setOrder(data);
      setError("");
      setLastUpdate(new Date());
      const loc = data.rider_location;
      if (loc?.latitude && loc?.longitude) {
        initMap();
        updateRiderPosition(loc.latitude, loc.longitude, data);
      }
    } catch {
      setError("Order not found. Please check your tracking code.");
    } finally {
      setLoading(false);
    }
  }, [token, initMap, updateRiderPosition]);


  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 15000);
    return () => clearInterval(interval);
  }, [fetchOrder]);


  useEffect(() => { initMap(); }, [initMap]);


  // ── Pusher real-time ─────────────────────────────────────────────
  useEffect(() => {
    if (!order?.order?.id) return;
    if (pusherChannel.current) return;


    const Pusher = window.Pusher;
    if (!Pusher) return;


    const pusher = new Pusher('6fedb8863d66bca87fce', {
      cluster: 'ap2',
      forceTLS: true,
      authEndpoint: 'https://tumatrack-api.onrender.com/api/broadcasting/auth',
      auth: {
        headers: {
          // Use token if logged in (rider/admin), otherwise anonymous
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          Accept: 'application/json',
        },
      },
    });


    const channel = pusher.subscribe(`private-order.${order.order.id}`);
    pusherChannel.current = channel;


    channel.bind('order.updated', (data) => {
      setOrder((prev) => ({ ...prev, order: { ...prev.order, status: data.status } }));
      setLastUpdate(new Date());
    });


    channel.bind('rider.location', (data) => {
      setLastUpdate(new Date());
      setOrder((prev) => ({
        ...prev,
        rider_location: {
          latitude: data.latitude,
          longitude: data.longitude,
          recorded_at: data.recorded_at,
        },
      }));
      initMap();
      updateRiderPosition(data.latitude, data.longitude, orderRef.current);
    });


    return () => {
      channel.unbind_all();
      pusher.unsubscribe(`private-order.${order.order.id}`);
      pusherChannel.current = null;
    };
  }, [order?.order?.id, initMap, updateRiderPosition]);

  // ── Confirmation handler ─────────────────────────────────────────
  const handleConfirmDelivery = async () => {
    setConfirming(true);
    try {
      await api.post(`/track/${token}/confirm`, {
        rating: rating || null,
        feedback: feedback || null,
      });
      setConfirmed(true);
      setOrder((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          customer_confirmed_at: new Date().toISOString(),
          customer_rating: rating || null,
        },
      }));
    } catch {
      // silently fail — let them try again
    } finally {
      setConfirming(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────
  const currentStepIndex = STATUS_STEPS.findIndex((s) => s.key === order?.order?.status);
  const isCancelled = order?.order?.status === "cancelled";
  const isDelivered = order?.order?.status === "delivered";
  const hasRiderLocation = !!order?.rider_location?.latitude;
  const hasDeliveryCoords = !!order?.order?.delivery_lat;


  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-gray-400">
        <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <p className="text-sm font-medium">Loading your order...</p>
      </div>
    </div>
  );


  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-5">
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-5">❌</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Order Not Found</h2>
        <p className="text-gray-500 text-sm mb-6">{error}</p>
        <Link to="/" className="inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm hover:bg-orange-600 transition-colors">
          Go Home
        </Link>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>


      {/* NAVBAR */}
      <nav className="bg-slate-900 px-5 h-14 flex items-center justify-between sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-white" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
          </div>
          <span className="text-white font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
            Tuma<span className="text-orange-400">Track</span>
          </span>
        </Link>
        <div className="flex items-center gap-1.5 text-xs font-medium text-green-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          Live Tracking
        </div>
      </nav>


      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">


        {/* CANCELLED BANNER */}
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
            <p className="text-3xl mb-2">❌</p>
            <p className="font-bold text-red-800">Order Cancelled</p>
            <p className="text-red-600 text-sm mt-1">This order has been cancelled.</p>
          </div>
        )}


        {/* ── DELIVERED — CONFIRMATION FLOW ── */}
        {isDelivered && (
          <>
            {/* Already confirmed by customer */}
            {(confirmed || order?.order?.customer_confirmed_at) ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <p className="text-4xl mb-3">🎉</p>
                <p className="font-bold text-green-800 text-lg" style={{ fontFamily: "'Sora', sans-serif" }}>
                  Delivery Confirmed!
                </p>
                <p className="text-green-600 text-sm mt-2 leading-relaxed">
                  Your package has been delivered and confirmed.<br />
                  Thank you for using TumaTrack.
                </p>
                {order?.order?.customer_rating && (
                  <div className="mt-4 flex justify-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`text-xl ${star <= order.order.customer_rating
                          ? 'text-yellow-400'
                          : 'text-gray-200'
                        }`}>★</span>
                    ))}
                  </div>
                )}
                {order?.order?.customer_confirmed_at && (
                  <p className="text-green-500 text-xs mt-3">
                    Confirmed at{" "}
                    {new Date(order.order.customer_confirmed_at)
                      .toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" })}
                    {" · "}
                    {new Date(order.order.customer_confirmed_at)
                      .toLocaleDateString("en-KE", { day: "numeric", month: "short" })}
                  </p>
                )}
              </div>
            ) : (
              /* Awaiting customer confirmation */
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                <div className="text-center mb-5">
                  <p className="text-3xl mb-2">📦</p>
                  <p className="font-bold text-gray-900 text-lg" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Your order has arrived!
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Please confirm you have received your package
                  </p>
                </div>

                {/* Star rating */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 text-center mb-2 uppercase tracking-wide">
                    Rate your delivery (optional)
                  </p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(rating === star ? 0 : star)}
                        className={`text-3xl transition-transform hover:scale-110 ${star <= rating ? 'text-yellow-400' : 'text-gray-200'
                          }`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional feedback */}
                {rating > 0 && (
                  <div className="mb-4">
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Share your experience (optional)..."
                      rows={2}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none text-gray-700"
                    />
                  </div>
                )}

                {/* Confirm button */}
                <button
                  onClick={handleConfirmDelivery}
                  disabled={confirming}
                  className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {confirming ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Confirming...
                    </>
                  ) : (
                    <>✓ Yes, I received my package</>
                  )}
                </button>

                <p className="text-gray-400 text-xs text-center mt-3">
                  Only confirm if you have physically received your order
                </p>
              </div>
            )}
          </>
        )}


        {/* ── SHOP CARD ── */}
        {order?.shop && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-gray-400 text-xs uppercase tracking-widest mb-3 font-semibold">
              Fulfilled by
            </p>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Logo or fallback */}
                <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-100 shrink-0 bg-orange-50 flex items-center justify-center">
                  {order.shop.logo ? (
                    <img
                      src={order.shop.logo}
                      alt={order.shop.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-xl">🏪</span>
                  )}
                </div>

                <div>
                  <p className="font-bold text-gray-900 text-base">
                    {order.shop.name}
                  </p>

                  {order.shop.description && (
                    <p className="text-gray-400 text-xs mt-0.5 max-w-[200px] line-clamp-2">
                      {order.shop.description}
                    </p>
                  )}

                  {!order.shop.description && order.shop.phone && (
                    <p className="text-gray-400 text-xs mt-0.5">
                      {order.shop.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Call */}
                <a
                  href={`tel:${order.shop.phone}`}
                  className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors"
                  title="Call shop"
                >
                  📞
                </a>

                {/* WhatsApp */}
                <a
                  href={`https://wa.me/254${(
                    order.shop.whatsapp || order.shop.phone
                  )?.replace(/^0/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center hover:bg-green-100 transition-colors"
                  title="WhatsApp shop"
                >
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ── ORDER HEADER CARD ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-gray-400 text-xs mb-1">Tracking Code</p>
              <p className="font-mono font-bold text-gray-900 text-lg">{token}</p>
            </div>
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full capitalize ${isCancelled ? "bg-red-100 text-red-600"
                : isDelivered ? "bg-green-100 text-green-600"
                  : "bg-orange-100 text-orange-600"
              }`}>
              {order?.order?.status?.replace("_", " ")}
            </span>
          </div>


          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Customer</p>
              <p className="font-semibold text-gray-900 text-sm">{order?.order?.customer_name}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Order Placed</p>
              <p className="font-semibold text-gray-900 text-sm">
                {order?.order?.created_at
                  ? new Date(order.order.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })
                  : "—"}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-gray-400 text-xs mb-0.5">Delivery Address</p>
              <p className="font-semibold text-gray-900 text-sm">{order?.order?.address}</p>
            </div>
            {order?.order?.delivery_fee && (
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Delivery Fee</p>
                <p className="font-semibold text-gray-900 text-sm">
                  KES {Number(order.order.delivery_fee).toLocaleString()}
                </p>
              </div>
            )}
            {lastUpdate && (
              <div>
                <p className="text-gray-400 text-xs mb-0.5">Last Updated</p>
                <p className="font-semibold text-green-600 text-sm">
                  {lastUpdate.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            )}
          </div>


          {/* Order items */}
          {order?.order?.items && (
            <div className="mt-4 pt-4 border-t border-gray-50">
              <p className="text-gray-400 text-xs mb-1.5">Items Ordered</p>
              <p className="text-gray-900 text-sm font-medium">{order.order.items}</p>
            </div>
          )}
        </div>


        {/* ── ETA CARD ── */}
        {hasRiderLocation && !isCancelled && !isDelivered && hasDeliveryCoords && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs mb-1 uppercase tracking-widest">Estimated Arrival</p>
                {etaLoading ? (
                  <p className="text-gray-400 text-sm animate-pulse">Calculating route...</p>
                ) : eta ? (
                  <p className="font-bold text-gray-900 text-2xl" style={{ fontFamily: "'Sora', sans-serif" }}>
                    {formatEta(eta.seconds)}
                  </p>
                ) : (
                  <p className="text-gray-400 text-sm">Route unavailable</p>
                )}
                {eta && !etaLoading && (
                  <p className="text-gray-400 text-xs mt-0.5">{formatDistance(eta.meters)} away by road</p>
                )}
              </div>
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center text-2xl shrink-0">
                🗺️
              </div>
            </div>
          </div>
        )}


        {/* ── STATUS TIMELINE ── */}
        {!isCancelled && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-5 text-sm uppercase tracking-widest text-gray-400">
              Delivery Progress
            </h2>
            <div className="space-y-0">
              {STATUS_STEPS.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isActive = index === currentStepIndex;
                const isLast = index === STATUS_STEPS.length - 1;
                return (
                  <div key={step.key} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 transition-all ${isCompleted
                          ? "bg-orange-500 border-orange-500 shadow-md shadow-orange-200"
                          : "bg-gray-50 border-gray-200"
                        }`}>
                        {isCompleted ? step.icon : "○"}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 h-8 mt-1 transition-colors ${index < currentStepIndex ? "bg-orange-400" : "bg-gray-200"
                          }`} />
                      )}
                    </div>
                    <div className={`pb-8 ${isLast ? "pb-0" : ""} pt-2`}>
                      <p className={`font-bold text-sm ${isCompleted ? "text-gray-900" : "text-gray-400"}`}>
                        {step.label}
                        {isActive && (
                          <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-semibold">
                            Current
                          </span>
                        )}
                      </p>
                      <p className={`text-xs mt-0.5 ${isCompleted ? "text-gray-500" : "text-gray-300"}`}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* ── DRIVER CARD ── */}
        {order?.rider && !isCancelled && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-widest text-gray-400">
              Your Driver
            </h2>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center text-2xl shrink-0">
                {vehicleIcon(order.rider.vehicle_type)}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-base">{order.rider.name}</p>
                {order.rider.vehicle_type && (
                  <p className="text-gray-500 text-sm capitalize mt-0.5">
                    {vehicleIcon(order.rider.vehicle_type)} {order.rider.vehicle_type}
                  </p>
                )}
                <p className="text-gray-400 text-xs mt-0.5">{order.rider.phone}</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Call rider */}
                {order.rider.phone && (
                  <a
                    href={`tel:${order.rider.phone}`}
                    className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors"
                    title="Call driver"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </a>
                )}
                {/* WhatsApp rider */}
                {order.rider.phone && (
                  <a
                    href={`https://wa.me/254${order.rider.phone?.replace(/^0/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center hover:bg-green-100 transition-colors"
                    title="WhatsApp driver"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}


        {/* ── LIVE MAP ── */}
        {hasRiderLocation && !isCancelled && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div>
              <h2 className="font-bold text-gray-900 text-sm uppercase tracking-widest text-gray-400">
                Live Location
              </h2>
              {order?.rider_location?.recorded_at && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Last ping at {formatTime(order.rider_location.recorded_at)}
                </p>
              )}
            </div>
            <div ref={mapRef} className="w-full h-64" />
          </div>
        )}


        {/* ── NOTES ── */}
        {order?.order?.notes && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
            <p className="text-amber-700 font-semibold text-sm mb-1">📝 Order Notes</p>
            <p className="text-amber-600 text-sm">{order.order.notes}</p>
          </div>
        )}


        {/* ── FOOTER ── */}
        <div className="text-center py-4">
          <p className="text-gray-400 text-xs">
            Powered by{" "}
            <Link to="/" className="text-orange-500 font-semibold hover:text-orange-600">TumaTrack</Link>
            {" "}· Updates in real time
          </p>
        </div>


      </div>
    </div>
  );
}